import { store } from '../store/store'
import { CONSTANT, SDK_TIMEOUT_MESSAGE } from '@/utils/constant'
import requestParameters from './request-parameters'
import responseParameters from './response-parameters'
import toasterService from '@/utils/toaster'
import messages from '../messages'
import traceService from '../components/shared/call-logs/trace-service'
import { sdkAdapters } from '../adapters'
import timeStampConverter from '@/utils/time-stamp-converter'
import { EventBus } from '@/event-bus'

const orchestration = {

  callMethod ({ enabledSdks = false, methodName, methodParameters = {} }) {
    if (!enabledSdks) {
      enabledSdks = store.getters.loadedSDKs
    }
    if (methodName === CONSTANT.TRACE.METHOD.INIT) {
      traceService.setNewTransaction()
    }
    methodParameters = requestParameters.getMethodParameters({ methodName, methodParameters })
    let checkoutMethod = store.getters.checkoutMethod || 'direct'
    let sdkCallingPromises
    if (checkoutMethod === 'direct') {
      if (methodName === CONSTANT.TRACE.METHOD.BOOTSTRAP) {
        sdkCallingPromises = this.bootstrap({ methodParameters })
      } else {
        sdkCallingPromises = this.directSdkCall({ methodName, methodParameters, enabledSdks })
      }
    }
    return this.executeSdkCalls({ methodName, sdkCallingPromises })
  },

  directSdkCall ({ methodName, methodParameters, enabledSdks }) {
    let promises = []
    let sdkName = 'mastercard'
    let sdkInstance = sdkAdapters[sdkName].getInstance()
    let requestBody = sdkAdapters[sdkName].requestBody.getRequestBody({ methodName, methodParameters })
    traceService.addRequestLogs({
      providerName: sdkName.toUpperCase(),
      methodName,
      params: requestBody
    })
    if (methodName === CONSTANT.TRACE.METHOD.CHECKOUT) {
      requestBody.windowRef = window.childSrcWindow
    }
    promises.push(
      this.getMethodCallPromise({ sdkName, sdkInstance, methodName, requestBody })
    )
    return promises
  },

  getMethodCallPromise ({ sdkName, sdkInstance, requestBody, methodName }) {
    let timeOut = (methodName === CONSTANT.TRACE.METHOD.CHECKOUT) ? 580000 : 200000
    return new Promise((resolve) => {
      let startTime = new Date()
      setTimeout(() => {
        let endTime = new Date()
        resolve({
          response: this.buildTimeoutResponse({ sdkName, timeOut }),
          timing: this.calculateSdkTime({ startTime, sdkName, endTime })
        })
      }, timeOut)
      sdkInstance[methodName](requestBody).then(value => {
        let endTime = new Date()
        resolve({
          response: this.buildSuccessResponse({ value, sdkName }),
          timing: this.calculateSdkTime({ startTime, sdkName, endTime })
        })
      }).catch(error => {
        let endTime = new Date()
        resolve({
          response: this.buildErrorResponse({ error, sdkName }),
          timing: this.calculateSdkTime({ startTime, sdkName, endTime })
        })
      })
    })
  },

  executeSdkCalls ({ sdkCallingPromises, methodName }) {
    return new Promise(function (resolve, reject) {
      Promise.all(sdkCallingPromises).then(sdkResponses => {
        let finalResponse = orchestration.getFinalResponse({ methodName, sdkResponses })
        let responseBody
        let checkoutMethod = store.getters.checkoutMethod || 'direct'
        if (checkoutMethod === 'direct') {
          responseBody = responseParameters.getMethodParameters({ methodName, finalResponse })
        }
        if (responseBody) {
          resolve(responseBody)
        } else {
          reject()
        }
      })
    })
  },

  getFinalResponse ({ sdkResponses, methodName }) {
    let finalResponse = {}
    let finalTimings = {}
    sdkResponses.forEach(sdkResponse => {
      if (typeof sdkResponse.response === 'string') {
        finalResponse = { ...finalResponse, ...sdkResponse }
      } else {
        finalResponse = { ...finalResponse, ...sdkResponse.response }
      }
      if (sdkResponse.timing) {
        finalTimings = { ...finalTimings, ...sdkResponse.timing }
      }
    })
    if (methodName !== CONSTANT.TRACE.METHOD.BOOTSTRAP) {
      traceService.addResponseLogs(methodName, finalResponse)
      traceService.addTimeTaken(methodName, finalTimings)
    }

    EventBus.$emit('traceLogEvent')
    return finalResponse
  },

  buildSuccessResponse ({ value, sdkName }) {
    return {
      [sdkName]: {
        status: 'fulfilled',
        response: value
      }
    }
  },

  buildTimeoutResponse ({ sdkName, timeOut }) {
    return {
      [sdkName]: {
        status: 'rejected',
        error: SDK_TIMEOUT_MESSAGE + (timeOut / 1000) + ' Seconds.'
      }
    }
  },

  buildErrorResponse ({ error, sdkName }) {
    return {
      [sdkName]: {
        status: 'rejected',
        response: error
      }
    }
  },

  bootstrap ({ methodParameters }) {
    let promises = []
    Object.keys(methodParameters).forEach(sdkName => {
      promises.push(
        orchestration.buildDynamicScriptTag({
          sdkJsUrl: methodParameters[sdkName].URL,
          sdkName
        })
      )
    })
    return promises
  },

  calculateSdkTime ({ sdkName, startTime, endTime }) {
    let totalTimeTakenInMS = Math.trunc(endTime.getTime() - startTime.getTime())
    const timeTakenData = {
      'Start Time': timeStampConverter.getDateAndTime(startTime),
      'End Time': timeStampConverter.getDateAndTime(endTime),
      'Total Time Taken In MS': totalTimeTakenInMS
    }
    return {
      [sdkName]: timeTakenData
    }
  },

  /**
   * building Dynamic script tag
   * @param srcNetwork
   * @returns {Promise<unknown>}
   */
  buildDynamicScriptTag ({ sdkJsUrl, sdkName }) {
    let SdkElementId = sdkName.toLowerCase() + 'SDK'
    let oldSdkElement = document.getElementById(SdkElementId)
    let oldSdkJsUrl = ''
    if (oldSdkElement) {
      oldSdkJsUrl = (oldSdkElement.src || '').trim()
    }
    if (sdkJsUrl !== oldSdkJsUrl) {
      orchestration.deleteScript('#' + SdkElementId)
      return this.loaderScript({ sdkJsUrl, sdkName })
    }
  },
  /**
   * loading dynamic script and necessary action on fail
   * @param scriptUrl
   * @param srcNetwork
   * @returns {Promise<unknown>}
   */
  loaderScript ({ sdkJsUrl, sdkName }) {
    return new Promise((resolve) => {
      let script = document.createElement('script')
      script.src = sdkJsUrl
      script.type = 'text/javascript'
      script.id = sdkName.toLowerCase() + 'SDK'
      let timerId = setTimeout(() => {
        resolve({
          response: ({
            [sdkName]: {
              'loaded': false,
              'error': 'TIMEOUT'
            }
          })
        })
      }, 30000)
      script.onload = () => {
        clearTimeout(timerId)
        resolve({
          response: ({
            [sdkName]: {
              'loaded': true
            }
          })
        })
      }
      script.onerror = (error) => {
        toasterService.onFailure(sdkName + messages['en-US'].errorMessages.SDK_LOAD)
        clearTimeout(timerId)
        resolve(resolve({
          response: ({
            [sdkName]: {
              'loaded': false,
              error
            }
          })
        }))
      }
      document.head.appendChild(script)
    })
  },

  /**
   * Delete existing sdk script
   * @param id
   */
  deleteScript (id) {
    const currentScript = document.querySelector(id)
    if (currentScript) {
      currentScript.parentNode.removeChild(currentScript)
    }
  }

}

export default orchestration
